import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Coins, 
  Music, 
  Loader2, 
  Sparkles,
  Crown,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";

export default function BuyCreditsModal({ open, onOpenChange, userCredits = 0 }) {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(null);

  useEffect(() => {
    if (open) {
      fetchPackages();
    }
  }, [open]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/credits/packages`);
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error("Failed to fetch packages", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    setPurchaseLoading(packageId);
    try {
      const response = await axios.post(`${API}/credits/purchase`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate purchase");
    } finally {
      setPurchaseLoading(null);
    }
  };

  const handleViewAllOptions = () => {
    onOpenChange(false);
    navigate("/buy-credits");
  };

  const handleSubscribe = () => {
    onOpenChange(false);
    navigate("/subscribe");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="buy-credits-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gold/20 rounded-full">
              <Coins className="h-5 w-5 text-gold" />
            </div>
            <DialogTitle className="font-serif text-xl">Get Song Credits</DialogTitle>
          </div>
          <DialogDescription>
            Purchase credits to stream this song and more. Credits never expire.
          </DialogDescription>
        </DialogHeader>

        {/* Current Balance */}
        {userCredits > 0 && (
          <div className="flex items-center justify-between p-3 bg-gold/10 rounded-lg border border-gold/20">
            <span className="text-sm text-muted-foreground">Your balance:</span>
            <span className="font-bold text-gold">{userCredits} credits</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gold" />
          </div>
        ) : (
          <div className="space-y-3">
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`flex items-center justify-between p-4 rounded-lg border transition-colors hover:bg-accent/50 ${
                  pkg.id === 'plus' ? 'border-gold bg-gold/5' : 'border-border'
                }`}
                data-testid={`modal-package-${pkg.id}`}
              >
                <div className="flex items-center gap-3">
                  <Music className="h-5 w-5 text-gold" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{pkg.name}</span>
                      {pkg.id === 'plus' && (
                        <Badge variant="outline" className="text-xs border-gold text-gold">
                          <Sparkles className="h-2.5 w-2.5 mr-1" />
                          Best Value
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      ${(pkg.price / pkg.audio_credits).toFixed(2)}/stream
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={pkg.id === 'plus' ? 'default' : 'outline'}
                  className={pkg.id === 'plus' ? 'bg-gold text-primary-foreground hover:bg-gold/90' : ''}
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchaseLoading !== null}
                  data-testid={`modal-buy-${pkg.id}-btn`}
                >
                  {purchaseLoading === pkg.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    `$${pkg.price}`
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or</span>
          </div>
        </div>

        {/* Subscription CTA */}
        <div 
          className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-gold/10 to-amber-500/10 border border-gold/30 cursor-pointer hover:from-gold/15 hover:to-amber-500/15 transition-colors"
          onClick={handleSubscribe}
          data-testid="modal-subscribe-cta"
        >
          <div className="flex items-center gap-3">
            <Crown className="h-5 w-5 text-gold" />
            <div>
              <span className="font-medium">Go Unlimited</span>
              <p className="text-xs text-muted-foreground">$29/month • Cancel anytime</p>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-gold" />
        </div>

        {/* View All Link */}
        <Button 
          variant="ghost" 
          className="w-full text-sm"
          onClick={handleViewAllOptions}
          data-testid="view-all-options-btn"
        >
          View all credit options
        </Button>
      </DialogContent>
    </Dialog>
  );
}
